import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api } from "Config/Api";
import { API_ENDPOINTS } from "Config/apiConfig";
import { ExtractErrorInfo } from "components/shared/ExtractErrorInfo";
import { ErrorType } from "types/errorType";
import { AssignIncidentActionType } from "types/assignIncidentActionType";

export interface IncidentActionState {
  loading: boolean;
  error: ErrorType | null;
  actionData: AssignIncidentActionType | null;
}

const initialState: IncidentActionState = {
  loading: false,
  error: null,
  actionData: null,
};

// Async thunk to assign an incident action
export const assignIncidentAction = createAsyncThunk<
  void,
  { incidentId: string; actionData: AssignIncidentActionType },
  { rejectValue: ErrorType }
>("incidentAction/assignIncidentAction", async ({ incidentId, actionData }, { rejectWithValue }) => {
  try {
    await api.post(API_ENDPOINTS.incidentActions(incidentId), actionData);
  } catch (error: any) {
    const errorInfo = ExtractErrorInfo(error);
    return rejectWithValue(errorInfo);
  }
});

// Async thunk to complete an incident action
export const completeIncidentAction = createAsyncThunk<
  void,
  { incidentId: string; actionId: string; reason: string },
  { rejectValue: ErrorType }
>("incidentAction/completeIncidentAction", async ({ incidentId, actionId, reason }, { rejectWithValue }) => {
  try {
    await api.patch(API_ENDPOINTS.completeIncidentAction(incidentId, actionId), { completionReason: reason });
  } catch (error: any) {
    const errorInfo = ExtractErrorInfo(error);
    return rejectWithValue(errorInfo);
  }
});

export const incidentActionSlice = createSlice({
  name: "incidentAction",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearActionData: (state) => {
      state.actionData = null;
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(assignIncidentAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignIncidentAction.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(assignIncidentAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as ErrorType;
      })
      .addCase(completeIncidentAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeIncidentAction.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(completeIncidentAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as ErrorType;
      });
  },
});

export const { clearError, clearActionData } = incidentActionSlice.actions;
export default incidentActionSlice.reducer;
